import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, ShieldCheck, Target, Wallet } from 'lucide-react';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import logo from '../../assets/logo-new.png';

const ICONS = {
  shield: ShieldCheck,
  wallet: Wallet,
  target: Target,
};

function renderDefinitionItem(item) {
  const match = item.match(/^"([^"]+)":\s*(.*)$/);

  if (!match) {
    return (
      <li key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-left text-base leading-8 text-slate-700">
        {item}
      </li>
    );
  }

  const [, term, description] = match;

  return (
    <li key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-left text-base leading-8 text-slate-700">
      <strong className="font-semibold text-slate-900">{term}:</strong>{' '}
      <span>{description}</span>
    </li>
  );
}

function renderSectionContent(section) {
  if (section.id === 'definicoes') {
    const [intro, ...items] = section.content;

    return (
      <div className="space-y-5">
        <p className="text-left text-lg leading-8 text-slate-700">{intro}</p>
        <ul className="space-y-3">
          {items.map((item) => renderDefinitionItem(item))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {section.content.map((paragraph) => (
        <p key={paragraph} className="text-left text-lg leading-8 text-slate-700">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

export default function TermsOfUse() {
  const { t } = useTranslation('terms');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const highlights = t('highlights', { returnObjects: true }) || [];
  const scopeContent = t('scopeContent', { returnObjects: true }) || [];
  const sections = t('sections', { returnObjects: true }) || [];
  const title = t('title');

  return (
    <div className="bg-slate-50">
      <header className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-cyan-200 rounded-full mix-blend-overlay filter blur-3xl" />
        </div>

       <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <Link to="/" className="inline-flex items-center">
                <img src={logo} alt={title} className="w-36 sm:w-40" />
              </Link>

              <div className="flex items-center gap-4">
                <LanguageSwitcher variant="landing" />
              </div>
            </header>
          <div className="max-w-2xl mt-24 lg:mt-28 pb-16 lg:pb-20">
            <p className="text-base font-semibold uppercase tracking-[0.24em] text-blue-100">
             {t('hero.eyebrow')}
            </p>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {title}
          </h1>

          <p className="mt-6 text-base leading-8 text-white/90 sm:text-base">
            {t('description')}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
           <Button
              size="lg"
              className="!bg-white !text-blue-700 !opacity-100 hover:!bg-gray-100 shadow-none">
            <Link to="/signin?mode=register">{t('createAccount')}</Link>
          </Button>

            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            >
              <Link to="/signin">{t('signIn')}</Link>
            </Button>
          </div>
        </div>
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 h-80 w-full bg-[linear-gradient(to_bottom,rgba(37,99,235,0)_0%,rgba(37,99,235,0.08)_24%,rgba(37,99,235,0.22)_46%,rgba(37,99,235,0.16)_64%,rgba(255,255,255,0.55)_84%,#ffffff_100%)]" />
      </header>

     <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {highlights.map((item) => {
              const Icon = ICONS[item.iconKey];

              return (
                <Card key={item.title} className="rounded-2xl border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700">
                        {Icon && <Icon className="h-5 w-5 text-white" />}
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-base font-semibold text-slate-900">{item.title}</h2>
                        <p className="text-sm leading-6 text-slate-600">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

         <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-200 pb-5 text-left">
               <CardTitle className="text-base sm:text-xl font-semibold text-slate-900">{t('scopeTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6 sm:p-8">
              {scopeContent.map((paragraph) => (
                <p key={paragraph} className="text-left text-base leading-8 text-slate-700">
                  {paragraph}
                </p>
              ))}
            </CardContent>
          </Card>

          {sections.map((section) => (
            <Card key={section.id} id={section.id} className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-200 pb-5 text-left">
                <CardTitle className="text-lg sm:text-xl font-semibold text-slate-900">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="p-6 sm:p-8">
                {renderSectionContent(section)}
              </CardContent>
            </Card>
          ))}

         <Card className="rounded-2xl border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">
            <CardContent className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
              <div className="text-center sm:text-left">
                <p className="text-base font-semibold text-slate-900">{t('privacyTitle')}</p>
                <p className="mt-2 text-base leading-6 text-slate-600">
                  {t('privacyDescription')}
                </p>
              </div>

              <Button asChild className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800">
                <Link to="/pages/politica-de-privacidade"
                onClick={() => window.scrollTo(0, 0)}>
                  <Mail className="mr-2 h-4 w-4" />
                  {t('privacyButton')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
